//@ts-nocheck
import { parseISO, getHours, getMinutes } from 'date-fns';
import { t } from 'i18next';

export const isValidDate = (date) => {
  if (!date) return false;

  const parsedDate = date instanceof Date ? date : new Date(date);

  return parsedDate instanceof Date && !isNaN(parsedDate.getTime());
};

export const getDateWithoutOffset = (date) => {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
};

export const parseISODateWithoutOffset = (date) => {
  return date && typeof date === 'string' ? parseISO(date.split('T')[0]) : date;
};

export const getDayOfWeekName = (date) => {
  if (!date) {
    return '';
  }

  return date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
};

// Converts a local date to UTC time in ISO format
// Used for converting local date to UTC time in ISO format and pass to the API
export const utcTimeISO = (localDate) => {
  if (!localDate) return;

  const hours = getHours(localDate);
  const minutes = getMinutes(localDate);

  // We create a new date with Date.UTC using the same date and time, but treating it as UTC
  const utcDate = new Date(
    Date.UTC(
      localDate.getFullYear(),
      localDate.getMonth(),
      localDate.getDate(),
      hours,
      minutes,
      0
    )
  );

  //  Now we can use .toISOString() or formatISO of date-fns
  const iso = utcDate.toISOString();

  return iso;
};

/* removeTimeFromDate
 *
 * Takes an input as a date, parses it only keeping the year, month, and day, then returns a correct UTC date.
 * See XGE-2058.
 * Intended to counteract local times in MUI DatePicker values (see RenderField component for dates)
 * to ensure that dates are stored correctly in the DB, regardless of the user's current timezone.
 */
export const removeTimeFromDate = (input) => {
  if (!input) return null;

  // Create a Date object from the input string
  const date = new Date(input);

  if (isNaN(date.getTime())) {
    return null; // Invalid date
  }

  // Extract the year, month, and date
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-based
  const day = date.getDate();

  // Return a new Date at midnight UTC
  const output = new Date(Date.UTC(year, month, day));
  return output;
};

// Convert the UTC string to a Date with UTC time but local time
export const utcStringToLocalDate = (utcString) => {
  if (!utcString) return null;
  // Parse the UTC string to a Date object
  const utcDate = new Date(utcString);
  if (isNaN(utcDate.getTime())) {
    return null; // Invalid date
  }
  // Create a new Date object with the same time but in local timezone
  const localDate = new Date(
    utcDate.getUTCFullYear(),
    utcDate.getUTCMonth(),
    utcDate.getUTCDate(),
    utcDate.getUTCHours(),
    utcDate.getUTCMinutes(),
    utcDate.getUTCSeconds()
  );

  return localDate;
};

export const formatDateWithAddedDay = (
  date,
  daysToAdd = 0,
  locale = 'en-US',
  options = { month: 'long', year: 'numeric' }
) => {
  const dateWithDay = new Date(date);
  if (daysToAdd > 0) {
    dateWithDay.setDate(dateWithDay.getDate() + daysToAdd);
  }

  const formatter = new Intl.DateTimeFormat(locale, options);
  const formatDay = (date) => String(date.getDate()).padStart(2, '0');

  return `${formatDay(dateWithDay)} ${formatter.format(dateWithDay)}`;
};

export const formatDateRange = (
  startDate,
  endDate,
  locale = 'en-US',
  options = { month: 'long', year: 'numeric' }
) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (!startDate || !endDate) return '';

  return `${formatDateWithAddedDay(start, 0, locale, options)} - ${formatDateWithAddedDay(end, 0, locale, options)}`;
};

export const formatDateWithAddedDays = (
  startDate,
  daysToAdd,
  locale,
  options
) => {
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + daysToAdd);
  return formatDateRange(startDate, endDate, { locale, options });
};

export const formatDateWithLocale = (
  date,
  locale = 'en-US',
  options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }
) => {
  if (!date || Number.isNaN(Date.parse(date))) {
    return date;
  }

  const formatter = new Intl.DateTimeFormat(locale, options);
  return formatter.format(new Date(date));
};

export const formatDate = (dateString, format) => {
  // Ensure a value is provided
  if (!isValidDate(dateString)) return '';

  const date = new Date(dateString);

  const pad = (num, size = 2) => num.toString().padStart(size, '0');

  const formatMap = {
    YYYY: date.getUTCFullYear().toString(),
    MM: pad(date.getUTCMonth() + 1),
    DD: pad(date.getUTCDate()),
    HH: pad(date.getUTCHours()),
    mm: pad(date.getUTCMinutes()),
    ss: pad(date.getUTCSeconds())
  };

  return format.replace(/YYYY|MM|DD|HH|mm|ss/g, (match) => formatMap[match]);
};

export const extractHourAndMinutesUTC = (isoDateString) => {
  if (!isValidDate(isoDateString)) return '';
  const date = new Date(isoDateString);
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  return `${hours} ${hours ? t('common:hours').toUpperCase() : ''} ${t('common:and')} ${minutes} ${minutes ? t('common:minutes').toUpperCase() : ''}`;
};

export const formatDurationFromDate = (dateString) => {
  if (!isValidDate(dateString)) return '';
  const match = dateString.toString().match(/\d{2}:\d{2}:\d{2}/);
  const timePart = match ? match[0] : null;
  if (!timePart) return '';
  const [hours, minutes] = timePart.split(':');
  const formattedHours = hours;
  const formattedMinutes = minutes > 0 ? `:${minutes}` : '';
  const formattedTime = `${formattedHours}${formattedMinutes}`;
  return formattedTime;
};

export const formatFullDate = (
  date,
  language = 'en-US',
  timeZone = 'Europe/London'
) => {
  // Convert to the specified timezone
  const options = { timeZone, hour12: true };
  const localDate = new Date(date.toLocaleString(language, options));

  // Extract date parts manually
  const day = localDate.getDate();
  const month = localDate.toLocaleString(language, { month: 'short' });
  const year = localDate.getFullYear();
  const time = formatTime(localDate);

  const tz =
    new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
      .formatToParts(localDate)
      .find((part) => part.type === 'timeZoneName')?.value || '';

  return `${day} ${month} ${year} ${time} ${tz}`;
};

export const formatDateWithTimezone = (
  dateValue,
  timeZone = 'Europe/London'
) => {
  // Ensure a value is provided
  if (!isValidDate(dateValue)) return '';

  // Convert both date and time to the correct timezone
  const options = { timeZone, hour12: true };
  const localDate = new Date(dateValue.toLocaleString('en-US', options));

  // Extract date parts
  const day = localDate.getDate();
  const month = localDate.toLocaleString('en-US', { month: 'short' });
  const year = localDate.getFullYear();

  return `${day} ${month} ${year} ${formatTimeWithTimezone(dateValue, timeZone)}`;
};

export const formatDateTimeWithTimezone = (
  dateValue,
  timeValue,
  timeZone = 'Europe/London'
) => {
  // Ensure a value is provided
  if (!isValidDate(dateValue))
    return formatTimeWithTimezone(timeValue, timeZone);

  // Convert both date and time to the correct timezone
  const options = { timeZone, hour12: true };
  const localDate = new Date(dateValue.toLocaleString('en-US', options));

  // Extract date parts
  const day = localDate.getDate();
  const month = localDate.toLocaleString('en-US', { month: 'short' });
  const year = localDate.getFullYear();

  return `${day} ${month} ${year} ${formatTimeWithTimezone(timeValue, timeZone)}`;
};

export const formatTimeWithTimezone = (
  timeValue,
  timeZone = 'Europe/London'
) => {
  // Ensure a value is provided
  if (!isValidDate(timeValue)) return '';

  const options = { timeZone, hour12: true };
  const localTime = new Date(timeValue.toLocaleString('en-US', options));
  const time = formatTime(timeValue);

  const tz =
    new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
      .formatToParts(localTime)
      .find((part) => part.type === 'timeZoneName')?.value || '';

  return `${time} ${tz}`;
};

export const formatDateTime = (dateValue, timeValue) => {
  const options = { hour12: true };
  let dateSegment = '';
  let timeSegment = '';

  if (isValidDate(dateValue)) {
    const localDate = new Date(dateValue?.toLocaleString('en-US', options));
    const day = localDate.getDate();
    const month = localDate.toLocaleString('en-US', { month: 'short' });
    const year = localDate.getFullYear();
    dateSegment = `${day} ${month} ${year}`;
  }

  if (isValidDate(timeValue)) {
    const date = new Date(timeValue);

    let hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();

    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours === 0 ? 12 : hours; // Convierte 0 a 12

    const formattedMinutes = minutes.toString().padStart(2, '0');
    timeSegment = `${hours}:${formattedMinutes} ${ampm}`;
  }

  return `${dateSegment} ${timeSegment}`;
};

export const formatTime = (timeValue) => {
  if (!isValidDate(timeValue)) return '';
  const match = timeValue.toString().match(/\d{2}:\d{2}:\d{2}/);
  const timePart = match ? match[0] : null;
  if (!timePart) return '';
  const [hours, minutes] = timePart.split(':');
  const amPm = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes > 0 ? `:${minutes}` : '';
  const formattedTime = `${formattedHours}${formattedMinutes}${amPm}`;
  return formattedTime;
};

/**
 * formatDateForDisplay
 *
 * Takes an input date, extracts the year/month/date, sets time to 12:00 UTC,
 * and displays it as "dd MMM yyyy" format.
 * This ensures consistent date display regardless of user's timezone.
 *
 * @param {Date|string} input - The input date
 * @returns {string} - Formatted date string (e.g., "25 Jul 2025")
 */
export const formatDateForDisplay = (input) => {
  if (!input) return '';

  const date = new Date(input);

  if (isNaN(date.getTime())) {
    return ''; // Invalid date
  }

  // Extract year, month, day from the input date
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-based
  const day = date.getDate();

  // Create UTC date at noon to avoid timezone issues
  const utcDate = new Date(Date.UTC(year, month, day, 12, 0, 0, 0));

  // Format as "dd MMM yyyy"
  return utcDate.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

/**
 * convertInputDate
 *
 * Takes an input date in local timezone, extracts the year/month/date,
 * sets time to 12:00 UTC, and returns it as a UTC datetime.
 * This is for converting user input dates to consistent UTC storage format.
 *
 * @param {Date|string} input - The input date in local timezone
 * @returns {Date} - UTC Date object set to noon UTC
 */
export const convertInputDate = (input) => {
  if (!input) return null;

  const date = new Date(input);

  if (isNaN(date.getTime())) {
    return null; // Invalid date
  }

  // Extract year, month, day from the input date (in user's local timezone)
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-based
  const day = date.getDate();

  // Create UTC date at noon (12:00 UTC) to avoid timezone boundary issues
  const utcDate = new Date(Date.UTC(year, month, day, 12, 0, 0, 0));

  return utcDate;
};

/**
 * formatDateWithAddedDayUTC
 *
 * UTC version of formatDateWithAddedDay that ensures consistent date calculation
 * regardless of user's timezone. Adds days to a date and formats as "dd Month yyyy".
 *
 * @param {Date|string} date - The base date
 * @param {number} daysToAdd - Number of days to add (default: 0)
 * @param {string} locale - Locale for formatting (default: 'en-US')
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted date string (e.g., "25 July 2025")
 */
export const formatDateWithAddedDayUTC = (
  date,
  daysToAdd = 0,
  locale = 'en-US',
  options = { month: 'long', year: 'numeric' }
) => {
  if (!date) return '';

  const inputDate = new Date(date);
  if (isNaN(inputDate.getTime())) {
    return '';
  }

  // Extract year, month, day and add days in UTC
  const year = inputDate.getFullYear();
  const month = inputDate.getMonth();
  const day = inputDate.getDate();

  // Create UTC date at noon and add days
  const utcDate = new Date(Date.UTC(year, month, day + daysToAdd, 12, 0, 0, 0));

  const formatter = new Intl.DateTimeFormat(locale, {
    ...options,
    timeZone: 'UTC'
  });
  const formatDay = (date) => String(date.getUTCDate()).padStart(2, '0');

  return `${formatDay(utcDate)} ${formatter.format(utcDate)}`;
};

/**
 * formatDateWithAddedDaysUTC
 *
 * UTC version of formatDateWithAddedDays that ensures consistent date range calculation
 * regardless of user's timezone. Creates a date range from start date to start date + days.
 *
 * @param {Date|string} startDate - The start date
 * @param {number} daysToAdd - Number of days to add for end date
 * @param {string} locale - Locale for formatting
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted date range string
 */
export const formatDateWithAddedDaysUTC = (
  startDate,
  daysToAdd,
  locale,
  options
) => {
  if (!startDate) return '';

  const inputDate = new Date(startDate);
  if (isNaN(inputDate.getTime())) {
    return '';
  }

  // Extract year, month, day from start date
  const year = inputDate.getFullYear();
  const month = inputDate.getMonth();
  const day = inputDate.getDate();

  // Create UTC start and end dates at noon
  const utcStartDate = new Date(Date.UTC(year, month, day, 12, 0, 0, 0));
  const utcEndDate = new Date(
    Date.UTC(year, month, day + daysToAdd, 12, 0, 0, 0)
  );

  return formatDateRange(utcStartDate, utcEndDate, { locale, options });
};
